import { useId, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getImportProposal, mergeImportProposal, scrapImportProposal, type ImportProposal } from "../../api/content/importProposalsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { toUiError, type UiError } from "../../errors/uiError";
import { useLoad } from "../../shared/loading/useLoad";
import { readStoredString, writeStoredString } from "../../shared/storage/storedString";
import { readStoredStringSet, writeStoredStringSet } from "../../shared/storage/storedStringSet";
import AppButton from "../../ui/buttons/AppButton";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import { carryForwardImpact, getImpactReport, type ImpactReport } from "./impactApi";
import ImpactReportView from "./ImpactReportView";
import ReviewWorking from "./ReviewWorking";
import Field from "../../ui/forms/Field";
import OptionLabel from "../../ui/forms/OptionLabel";

type Action = "merge" | "carry" | "scrap";
type DecisionState = { readonly status: "working" | "reconciling" | "uncertain" | "succeeded"; readonly action: Action; readonly error?: UiError; readonly count?: number } | null;

/** Timed-out writes are followed only by reads. Persist the guard across page reloads. */
export default function CorpusImpactReview({ value, onDecided }: { value: ImportProposal; onDecided: () => void }) {
  const { t } = useTranslation();
  const headingId = useId();
  const id = value.changeSet.changeSetId;
  const load = useCallback((signal: AbortSignal) => getImpactReport(id, signal), [id]);
  const { state: loaded, reload } = useLoad(load);
  const [report, setReport] = useState<ImpactReport | null>(null);
  const [proposal, setProposal] = useState(value);
  const [reason, setReason] = useState("");
  const [acknowledgement, setAcknowledgement] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const keyOf = (action: Action) => `dmw.corpus-review.${id}.${value.head}.${action}`;
  const [locked, setLocked] = useState(() => ({ merge: readStoredString(keyOf("merge")) !== null, carry: readStoredString(keyOf("carry")) !== null }));
  const [unconfirmed, setUnconfirmed] = useState(() => ["merge", "carry"].some((action) => readStoredString(`dmw.corpus-review.${id}.${value.head}.${action}`) === "submitted"));
  const [decision, setDecision] = useState<DecisionState>(null);
  const carryTargets = useRef(readStoredStringSet(`${keyOf("carry")}.targets`) ?? new Set<string>());
  const running = useRef(false);
  const active = decision?.status === "working" || decision?.status === "reconciling";
  const impact = report ?? (loaded.status === "loaded" ? loaded.value : null);
  const merged = proposal.changeSet.state === "merged";
  const open = proposal.changeSet.state === "open" || proposal.changeSet.state === "sent_back";
  const matching = impact?.head === proposal.head;
  const needsAcknowledgement = impact?.findings.some((finding) => finding.impact === "changed" || finding.impact === "removed") === true;
  const eligible = impact?.findings.filter((finding) => finding.bulkCarryForwardEligible).length ?? 0;

  const reconcile = async (action: Action) => {
    // Independent reads: a slow impact report must not hide a confirmed merge.
    const results = await Promise.allSettled([
      getImportProposal(id).then((fresh) => { setProposal(fresh); return fresh; }),
      getImpactReport(id).then((fresh) => { setReport(fresh); return fresh; }),
    ]);
    const [freshProposal, freshImpact] = results;
    if ((action === "merge" && freshProposal.status === "fulfilled" && freshProposal.value.changeSet.state === "merged") ||
        (action === "carry" && freshImpact.status === "fulfilled" && freshImpact.value.head === value.head && carryTargets.current.size > 0 && [...carryTargets.current].every((target) => freshImpact.value.findings.some((finding) => `${finding.module}:${finding.kind}:${finding.identifier}` === target && finding.decision === "carry_forward")))) {
      writeStoredString(keyOf(action), "completed");
      setUnconfirmed(false);
      setDecision({ status: "succeeded", action, count: freshImpact.status === "fulfilled" ? freshImpact.value.findings.filter((finding) => finding.decision === "carry_forward").length : 0 });
      if (action === "merge") onDecided();
      return;
    }
    const failed = results.find((result) => result.status === "rejected");
    setDecision({ status: "uncertain", action, ...(failed?.status === "rejected" ? { error: toUiError(failed.reason) } : {}) });
  };
  const checkState = () => {
    if (running.current) return;
    running.current = true;
    const action = decision?.action ?? (locked.merge && !merged ? "merge" : "carry");
    setDecision({ status: "reconciling", action });
    void reconcile(action).finally(() => { running.current = false; });
  };
  const submit = (action: Action) => {
    if (running.current || (action !== "scrap" && (locked[action] || readStoredString(keyOf(action)) !== null))) return;
    running.current = true;
    if (action !== "scrap") {
      if (action === "carry" && impact !== null) {
        carryTargets.current = new Set(impact.findings.filter((finding) => finding.bulkCarryForwardEligible).map((finding) => `${finding.module}:${finding.kind}:${finding.identifier}`));
        writeStoredStringSet(`${keyOf(action)}.targets`, carryTargets.current);
      }
      writeStoredString(keyOf(action), "submitted");
      setLocked((current) => ({ ...current, [action]: true }));
      setUnconfirmed(true);
    }
    setDecision({ status: "working", action });
    void (async () => {
      try {
        if (action === "carry") {
          await carryForwardImpact(id);
          setDecision({ status: "reconciling", action });
          await reconcile(action);
        } else {
          const fresh = action === "merge"
            ? await mergeImportProposal(id, proposal.head, reason.trim(), needsAcknowledgement ? acknowledgement.trim() : null)
            : await scrapImportProposal(id, reason.trim());
          setProposal(fresh);
          if (action === "merge") writeStoredString(keyOf(action), "completed");
          setUnconfirmed(false);
          setDecision({ status: "succeeded", action });
          onDecided();
        }
      } catch (caught) {
        setDecision({ status: "reconciling", action, error: toUiError(caught) });
        await reconcile(action);
      } finally {
        running.current = false;
      }
    })();
  };

  return <section className="app-card space-y-3 p-3" aria-labelledby={headingId}>
    <h3 id={headingId} className="app-title text-base font-semibold">{t("importProposals.impact.title")}</h3>
    {loaded.status === "loading" && report === null ? <ReviewWorking /> : null}
    {loaded.status === "failed" && report === null ? <ErrorNotice error={loaded.error} onRetry={reload} /> : null}
    {impact === null ? null : <ImpactReportView report={impact} />}
    {impact !== null && !matching ? <p className="app-notice-warning p-2">{t("importProposals.impact.stale")}</p> : null}
    {active ? <ReviewWorking /> : null}
    {decision?.error === undefined ? null : <ErrorNotice error={decision.error} />}
    {merged ? <p role="status">{t("importProposals.impact.merged")}</p> : null}
    {decision?.status === "succeeded" && decision.action === "carry" ? <p role="status">{t("importProposals.impact.carried", { count: decision.count })}</p> : null}
    {decision?.status === "uncertain" || (decision === null && unconfirmed) ? <div className="app-notice-warning space-y-2 rounded p-2 text-sm">
      <p>{t("importProposals.impact.notResubmitted")}</p>
      <p>{t(merged ? "importProposals.impact.mergeConfirmed" : "importProposals.impact.stillOpen")}</p>
      {impact === null ? null : <p>{t("importProposals.impact.recorded", { count: impact.findings.filter((finding) => finding.decision === "carry_forward").length, remaining: t("importProposals.impact.remaining", { count: eligible }) })}</p>}
      <AppButton size="sm" disabled={active} onClick={checkState}>{t("importProposals.impact.checkState")}</AppButton>
    </div> : null}
    {value.mayReview && open ? <div className="space-y-2">
      <Field label={t("importProposals.page.reason")}>
        {(control) => (
          <textarea {...control} className={FORM_CONTROL_CLASS} rows={2} maxLength={2000} value={reason} disabled={active} onChange={(event) => { setReason(event.target.value); }} />
        )}
      </Field>
      {needsAcknowledgement ? <>
        <OptionLabel align="start"><input type="checkbox" checked={acknowledged} disabled={active} onChange={(event) => { setAcknowledged(event.target.checked); }} />{t("importProposals.impact.acknowledge")}</OptionLabel>
        <Field label={t("importProposals.impact.acknowledgementNote")}>
          {(control) => (
            <textarea {...control} className={FORM_CONTROL_CLASS} rows={2} maxLength={2000} disabled={active} value={acknowledgement} onChange={(event) => { setAcknowledgement(event.target.value); }} />
          )}
        </Field>
      </> : null}
      <div className="flex gap-2">
        <AppButton size="sm" variant="primary" disabled={active || locked.merge || !matching || reason.trim() === "" || proposal.corpusMoved || (needsAcknowledgement && (!acknowledged || acknowledgement.trim() === ""))} onClick={() => { submit("merge"); }}>{t("importProposals.page.merge")}</AppButton>
        <AppButton size="sm" disabled={active || locked.merge || reason.trim() === ""} onClick={() => { submit("scrap"); }}>{t("importProposals.page.scrap")}</AppButton>
      </div>
    </div> : null}
    {value.mayReview && merged ? <AppButton size="sm" disabled={active || locked.carry || !matching || eligible === 0} onClick={() => { submit("carry"); }}>{t("importProposals.impact.carry", { count: eligible })}</AppButton> : null}
  </section>;
}
